import { useEffect, useRef, useState } from 'react';
import { dashboardFeatures } from '@/mocks/homeData';

export default function DashboardSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % dashboardFeatures.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section
      id="dashboard"
      ref={sectionRef}
      className="relative py-20 md:py-28 bg-firmai-darker overflow-hidden"
    >
      {/* Animated gradient orbs */}
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-firmai-green/5 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-firmai-blue/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left Content */}
          <div className="w-full lg:w-[40%] space-y-6">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
              <span className="text-firmai-green text-sm font-semibold tracking-wider uppercase">
                PLATFORM CORE
              </span>
              <h2 className="font-display font-bold text-2xl md:text-4xl text-white mt-3 leading-tight">
                实时监测
                <br />
                一目了然
              </h2>
            </div>

            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-100">
              <p className="text-firmai-text text-sm md:text-base leading-relaxed">
                平台提供7x24小时实时数据采集，覆盖电、水、气、热等多能源类型。多维度能耗分析帮助您快速定位异常，异常自动告警确保问题第一时间被发现和处理。
              </p>
            </div>

            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-200 space-y-3">
              {dashboardFeatures.map((feature, index) => (
                <div
                  key={feature.title}
                  onClick={() => setActiveFeature(index)}
                  className={`flex items-start gap-4 p-3 rounded-lg cursor-pointer transition-all duration-300 ${
                    activeFeature === index
                      ? 'bg-firmai-green/10 border border-firmai-green/20'
                      : 'hover:bg-white/5 border border-transparent'
                  }`}
                >
                  <span className={`w-8 h-8 flex items-center justify-center shrink-0 mt-0.5 rounded-md transition-all duration-300 ${
                    activeFeature === index
                      ? 'bg-firmai-green/20 text-firmai-green'
                      : 'text-firmai-muted'
                  }`}>
                    <i className={`${feature.icon} text-lg`} />
                  </span>
                  <div>
                    <h4 className={`font-medium text-sm md:text-base transition-colors duration-300 ${
                      activeFeature === index ? 'text-white' : 'text-firmai-text'
                    }`}>
                      {feature.title}
                    </h4>
                    <p className="text-firmai-muted text-xs md:text-sm mt-0.5">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-300">
              <a
                href="#demo"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-firmai-green text-firmai-dark font-semibold rounded-md hover:bg-firmai-green/90 hover:scale-105 active:scale-95 transition-all duration-200 whitespace-nowrap group"
              >
                查看完整功能
                <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-200">
                  <i className="ri-arrow-right-line" />
                </span>
              </a>
            </div>
          </div>

          {/* Right Dashboard Preview */}
          <div className="w-full lg:w-[60%]">
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-200 relative group">
              <div className="relative rounded-xl overflow-hidden shadow-2xl shadow-firmai-green/5 border border-firmai-border/50 group-hover:border-firmai-green/20 transition-all duration-500">
                <img
                  src="https://readdy.ai/api/search-image?query=comprehensive%20energy%20monitoring%20dashboard%20with%20multiple%20KPI%20cards%20line%20charts%20bar%20graphs%20device%20status%20grid%20dark%20theme%20UI%20green%20and%20blue%20accent%20colors%20professional%20enterprise%20software%20interface&width=700&height=500&seq=dashboard2&orientation=landscape"
                  alt="Firmai 实时监测仪表盘"
                  className="w-full h-auto object-cover group-hover:scale-[1.02] transition-transform duration-700"
                />
                {/* Scanning line effect */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-firmai-green/50 to-transparent animate-scan" />
                </div>
              </div>

              {/* Decorative elements with hover */}
              <div className="absolute -top-3 -right-3 w-20 h-20 border border-firmai-green/20 rounded-lg group-hover:border-firmai-green/40 transition-colors duration-300" />
              <div className="absolute -bottom-3 -left-3 w-20 h-20 border border-firmai-blue/20 rounded-lg group-hover:border-firmai-blue/40 transition-colors duration-300" />

              {/* Live indicator */}
              <div className="absolute top-4 right-4 flex items-center gap-2 bg-firmai-dark/80 backdrop-blur-sm border border-firmai-border rounded-full px-3 py-1.5">
                <span className="w-2 h-2 bg-firmai-green rounded-full animate-pulse" />
                <span className="text-firmai-green text-xs font-medium">实时数据</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .reveal.animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
        @keyframes scan {
          0% { top: 0; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        .animate-scan {
          animation: scan 3s linear infinite;
        }
      `}</style>
    </section>
  );
}